/**
 * FriendCardPending component
 * Displays pending invitation card with accept/decline actions or sent invitation status
 * Uses shared PendingInvitationCard from components/ui
 */
import React from 'react'
import { GestureResponderEvent } from 'react-native'
import { PendingInvitationCard } from '../../ui/PendingInvitationCard'
import { getSafeDisplayName } from '../../../src/create/helpers/createUi.helper'
import type { Friend } from '../../../src/friends/types/friends.types'

interface FriendCardPendingProps {
  friend: Friend
  /** Cancel/withdraw sent invitation; only passed when friend is pending (sent). */
  onRemoveFriend?: (friendId: string, friend?: Friend) => void
  onAcceptInvitation?: (token: string, invitationId: string) => void
  onDeclineInvitation?: (invitationId: string) => void
  actionLoadingId?: string | null
  pendingRequestCount?: number // Total count of pending requests (for badge display)
  requestIndex?: number // Index of this request (0-based) for stacking visualization
  /** Test ID for testing */
  testID?: string
}

export const FriendCardPending: React.FC<FriendCardPendingProps> = ({
  friend,
  onRemoveFriend,
  onAcceptInvitation,
  onDeclineInvitation,
  actionLoadingId,
  pendingRequestCount: _pendingRequestCount = 0,
  requestIndex: _requestIndex = 0,
  testID,
}) => {
  const handleRemovePress = (e: GestureResponderEvent) => {
    e.stopPropagation()
    onRemoveFriend?.(friend.id, friend)
  }

  if (friend.invitationType === 'received') {
    const hasToken = !!friend.invitationToken
    const hasInvitationId = !!friend.invitationId

    if (!hasToken || !hasInvitationId) {
      return (
        <PendingInvitationCard
          variant="received"
          title=""
          message=""
          onAccept={() => undefined}
          onDecline={() => undefined}
          errorMessage="Invalid invitation data. Please refresh the page."
          testID={testID || `friend-card-pending-received-${friend.id}`}
        />
      )
    }

    const inviterName = friend.inviterName || friend.name || friend.inviterEmail || 'Someone'
    const displayInviterName = getSafeDisplayName(inviterName, friend.inviterEmail || friend.email)
    const isLoading = actionLoadingId === friend.invitationId

    return (
      <PendingInvitationCard
        variant="received"
        title={displayInviterName}
        message="wants to be your friend"
        onAccept={() => {
          if (friend.invitationToken && friend.invitationId) {
            onAcceptInvitation?.(friend.invitationToken, friend.invitationId)
          }
        }}
        onDecline={() => {
          if (friend.invitationId) {
            onDeclineInvitation?.(friend.invitationId)
          }
        }}
        isLoading={isLoading}
        disabled={!hasToken || !hasInvitationId}
        testID={testID || `friend-card-pending-received-${friend.id}`}
      />
    )
  }

  // Sent invitation
  const primaryName = getSafeDisplayName(friend.name, friend.email)
  const secondaryText =
    friend.email && !friend.email.includes('@placeholder.com') ? friend.email : null

  return (
    <PendingInvitationCard
      variant="sent"
      primaryName={primaryName}
      statusText="Pending invitation"
      secondaryText={secondaryText}
      onRemove={onRemoveFriend ? handleRemovePress : undefined}
      testID={testID || `friend-card-pending-sent-${friend.id}`}
    />
  )
}
